<template>
    <Drawer
        ref="drawer"
        title="Revenue Entry"
        :loading="isLoading || isUploading"
        size="is-large"
        @closed="resetState()"
    >
        <template v-if="entry" #header>
            <div class="tw-flex tw-justify-between tw-items-center">
                <EditableText
                    v-model="entry.name"
                    class="h2"
                    :max-length="255"
                    :editable="isEditable"
                    @update:model-value="debouncedUpdateEntry()"
                />
                <Button
                    v-if="Auth.can('delete revenue entries') && !entry.category.is_pooled"
                    class="tw-flex tw-gap-2 tw-h-fit"
                    type="is-danger"
                    :disabled="invoices && !!invoices.length"
                    @click="deleteEntry()"
                >
                    <i class="mdi mdi-delete"></i>
                    Delete Entry
                </Button>
            </div>
            <div class="tw-flex tw-justify-between">
                <FormField label="Net Total">
                    {{ localise(entry.subtotal, 2, true) }}
                </FormField>
                <FormField label="Gross Total">
                    {{ localise(entry.total, 2, true) }}
                </FormField>
                <FormField v-if="!entry.category.is_pooled" label="Paid">
                    {{ localise(entry.total_paid, 2, true) }}
                </FormField>
            </div>
        </template>
        <template v-if="entry">
            <div class="tw-grid tw-grid-cols-2">
                <FormField v-if="!entry.category.is_pooled" label="Linked Budget Entry">
                    <div class="d-inline-flex">
                        <ModalSelector
                            v-model="entry.budget_entry_id"
                            :endpoint="
                                route('api.account.event.entries', {
                                    category_id: entry.category_id,
                                })
                            "
                            modal-title="Link Budget Entry"
                            button-title="Link a Budget Entry"
                            allow-search
                            :editable="isEditable"
                            @update:model-value="
                                (val) => {
                                    entry.budget_entry_id = val;
                                    updateEntry();
                                }
                            "
                        />
                        <Tooltip v-if="entry.budget_entry_id" label="View Budget Entry" class="pl-2">
                            <Button @click="openLinkedEntry">
                                <i class="mdi mdi-eye-outline"></i>
                            </Button>
                        </Tooltip>
                    </div>
                </FormField>
                <FormField v-if="entry.user" label="Created by">
                    <User :user="entry.user" :hide-name="false" />
                </FormField>
            </div>
            <div v-if="!entry.category.is_pooled" class="tw-grid tw-grid-cols-2 tw-mt-5">
                <FormField label="Subcategory">
                    <Selector
                        v-model="entry.category_id"
                        :options="availableCategories"
                        :editable="isEditable"
                        @update:model-value="updateEntry"
                    />
                </FormField>
                <FormField label="Customer">
                    <SupplierSelector
                        v-model="entry.supplier_id"
                        object-label="customer"
                        :editable="isEditable"
                        @update:model-value="
                            (val) => {
                                entry.supplier_id = val;
                                val ? updateEntry() : val;
                            }
                        "
                    />
                </FormField>
            </div>

            <div class="tw-flex tw-mt-3 tw-gap-3">
                <Tooltip
                    v-if="entry.budget_entry_id && !entry.category.is_pooled"
                    label="View Budget Entry"
                    class="tw-mt-[18px]"
                >
                    <Button @click="openLinkedEntry">
                        <i class="mdi mdi-eye-outline"></i>
                    </Button>
                </Tooltip>
            </div>

            <InputEdit
                v-if="!entry.category.is_pooled"
                v-model="entry.payment_terms_description"
                class="tw-mt-5"
                label="Custom Payment Terms"
                size="is-small"
                :editable="isEditable"
                @update:model-value="debouncedUpdateEntry"
            />
            <hr />
            <Tabs :tabs="visibleTabs" container-class="tw-overflow-y-auto">
                <template #tab(line_items)>
                    <LineItemBuilder
                        v-model="entry.line_items"
                        :editable="Auth.can('create revenue entries') && !entry.category.is_pooled && isEditable"
                        :hide-invoiced="entry.category.is_pooled"
                        tax-rate-type="income"
                        :default-tax-rate-id="
                            parseInt(
                                store.get(
                                    `current_event.revenue_departments.${entry.department_id}.default_tax_rate_id`
                                )
                            )
                        "
                        @update:model-value="updateLineItems"
                    />
                </template>
                <template v-if="!entry.category.is_pooled" #tab(activity)>
                    <DrawerCommentsTab
                        v-if="!entry.category.is_pooled"
                        :activity="entry.activity"
                        :comments="entry.comments"
                        :disabled="!isEditable"
                        @comment="(text) => addComment(text)"
                    />
                </template>
                <template v-if="!entry.category.is_pooled" #tab(files)>
                    <DrawerFilesTab
                        v-if="!entry.category.is_pooled"
                        :endpoint="
                            route('api.account.event.revenue.files.upload', {
                                revenueEntry: entry.id,
                            })
                        "
                        :complete-endpoint="
                            route('api.account.event.revenue.files.completeUpload', {
                                revenueEntry: entry.id,
                            })
                        "
                        :files="entry.files"
                        :disabled="!isEditable"
                        @updated="refreshData()"
                    />
                </template>
                <template v-if="!entry.category.is_pooled" #tab(invoices)>
                    <DrawerInvoicesTab
                        v-if="!entry.category.is_pooled"
                        :can-create-invoice="Auth.can('create customer invoices')"
                        :on-raise-invoice="listenOnRaiseXeroInvoice"
                        :invoices
                        :has-xero-account="!!entry.category.xero_account_id"
                        :can-raise-invoice-conditions="[
                            {
                                can: !!entry.supplier_id,
                                message: 'Please assign a customer in order to raise an invoice.',
                            },
                            {
                                can: !!hasInvoiceableItems,
                                message: '',
                            },
                            {
                                can: isEditable,
                                message: 'This revenue entry is no longer editable.',
                            },
                        ]"
                        @view="viewInvoice"
                    />
                </template>
            </Tabs>
            <RaiseSalesInvoice @reload="refreshData" @created="viewInvoice" />
        </template>
        <template v-if="entry" #footer>
            <Presence :channel="'RevenueEntry.' + entry.id" label="Currently viewing this Revenue Entry"></Presence>
        </template>
    </Drawer>
</template>
<script>
import debounce from 'lodash/debounce';
import Drawer from '@/js/components/Drawer.vue';
import Selector from '@/js/components/controls/Selector.vue';
import SupplierSelector from '@/js/components/controls/SupplierSelector.vue';
import LineItemBuilder from '@/js/components/widgets/LineItemBuilder.vue';
import RaiseSalesInvoice from '@/js/components/modals/Invoices/RaiseSalesInvoice.vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import Presence from '@/js/components/widgets/PresenceIndicator.vue';
import EditableText from '@/js/components/controls/EditableText.vue';
import Button from '@/js/components/controls/Button.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import Tabs from '@/js/components/widgets/Tabs.vue';
import DrawerFilesTab from '@/assets/js/eventbooks/components/drawer-tabs/files.vue';
import DrawerInvoicesTab from '@/assets/js/eventbooks/components/drawer-tabs/invoices.vue';
import DrawerCommentsTab from '@/assets/js/eventbooks/components/drawer-tabs/comments.vue';
import User from '@/js/components/widgets/User.vue';
import { toast } from '@/js/utils.js';
import InputEdit from '@/assets/js/eventbooks/components/input-edit.vue';
import ModalSelector from '@/assets/js/eventbooks/components/modals/selector.vue';

export default {
    components: {
        Drawer,
        Selector,
        SupplierSelector,
        LineItemBuilder,
        RaiseSalesInvoice,
        Presence,
        EditableText,
        Button,
        FormField,
        DrawerInvoicesTab,
        DrawerFilesTab,
        DrawerCommentsTab,
        Tooltip,
        Tabs,
        User,
        InputEdit,
        ModalSelector,
    },
    data() {
        const store = useDataStore();
        return {
            isLoading: true,
            isUploading: false,
            entryId: 0,
            entry: null,
            invoicesTable: null,
            invoices: [],
            store: store,
        };
    },
    computed: {
        isEditable() {
            if (this.entry && this.entry.current_rms_opportunity_item_id) {
                return false;
            }
            return Auth.can('update revenue entries') && !this.store.get('current_event.is_closed');
        },
        lineItemsAreValid() {
            if (!this.entry) {
                return false;
            }
            return !this.entry.line_items.find(
                (lineItem) => !lineItem.quantity || !lineItem.unit_price || !lineItem.description
            );
        },
        hasInvoiceableItems() {
            if (!this.entry) {
                return false;
            }
            return Object.values(this.entry.line_items).some(function (item) {
                return (
                    item.is_invoiced === false ||
                    (item.quantity > 0 &&
                        item.unit_price > 0 &&
                        item.description.length > 0 &&
                        !item.hasOwnProperty('is_invoiced'))
                );
            });
        },
        visibleTabs() {
            const tabs = [
                {
                    title: 'Line Items',
                    name: 'line_items',
                },
            ];
            if (this.entry && this.entry.category.is_pooled) {
                return tabs;
            }
            return tabs.concat([
                {
                    title: 'Activity',
                    name: 'activity',
                },
                {
                    title: 'Files',
                    name: 'files',
                },
                {
                    title: 'Invoices',
                    name: 'invoices',
                },
            ]);
        },
        availableCategories() {
            if (!this.entry) {
                return [];
            }
            const revenueDepartment = Object.values(this.store.get('current_event.revenue_departments')).find(
                (department) => department.id === this.entry.department_id
            );
            if (!revenueDepartment) {
                return [];
            }
            return Object.values(this.store.get('current_event.categories')).filter(
                (category) => category.department_id === revenueDepartment.id && !category.is_pooled
            );
        },
    },
    watch: {
        'entry.line_items': {
            handler(newVal) {
                this.updateLineItems(newVal);
            },
            deep: true,
        },
    },
    mounted() {
        Eventbus.$on('revenue:entry', (payload) => {
            this.entryId = payload.entry;
            this.refreshData(true);
            if (this.$refs.drawer) {
                this.$refs.drawer.open();
            }
        });
        Eventbus.$on('component-selector:updated', () => {
            this.$forceUpdate();
        });
        Eventbus.$on('open:RevenueEntry', (payload) => {
            this.entryId = payload;
            this.refreshData(true);
            if (this.$refs.drawer) {
                this.$refs.drawer.open();
            }
        });
        Eventbus.$on('reloadCustomerInvoices', () => {
            if (this.entry) {
                this.initialiseInvoicesTable();
            }
        });
    },
    methods: {
        listenOnRaiseXeroInvoice() {
            Eventbus.$emit('salesInvoice:create', {
                entry: this.entryId,
            });
        },
        async initialiseInvoicesTable() {
            if (!this.entry) {
                return;
            }
            axios
                .get(
                    route('api.account.event.revenue.invoices', {
                        revenueEntry: this.entry.id,
                    })
                )
                .then(({ data }) => {
                    this.invoices = data;
                });
        },
        closeModal() {
            this.isLoading = true;
            if (this.$refs.drawer) {
                this.$refs.drawer.close();
            }
            this.reloadRelatedComponents();
        },
        refreshData() {
            axios
                .get(
                    route('api.account.event.revenue.get', {
                        revenueEntry: this.entryId,
                    })
                )
                .then(({ data }) => {
                    this.entry = data;

                    if (Auth.can('view customer invoices')) {
                        this.initialiseInvoicesTable();
                    }
                })
                .finally(() => {
                    setTimeout(() => {
                        this.isLoading = false;
                    }, 200);
                });
        },
        deleteEntry() {
            axios
                .delete(
                    route('api.account.event.revenue.delete', {
                        revenueEntry: this.entryId,
                    })
                )
                .then(() => {
                    this.closeModal();
                    toast('Success', 'Revenue entry deleted', 'success');
                });
        },
        updateEntry() {
            if (this.isLoading) {
                return;
            }

            this.isUploading = true;
            const formData = this.entry;
            delete formData.comments;
            delete formData.files;
            delete formData.activity;

            axios
                .post(
                    route('api.account.event.revenue.update', {
                        revenueEntry: this.entry.id,
                    }),
                    formData
                )
                .then(({ data }) => {
                    this.entry = data;
                })
                .finally(() => {
                    this.reloadRelatedComponents();
                    this.isUploading = false;
                });
        },
        debouncedUpdateEntry: debounce(function () {
            this.updateEntry();
        }, 500),
        openLinkedEntry() {
            if (!this.entry.budget_entry_id) {
                return;
            }
            Eventbus.$emit('budget:entry', {
                event: this.entry.event_id,
                entry: this.entry.budget_entry_id,
                source: {
                    emitter: 'open:revenueEntry',
                    payload: this.entry.id,
                },
            });
        },
        addComment(text) {
            if (!text) {
                return;
            }
            this.isUploading = true;
            axios
                .post(
                    route('api.account.event.revenue.comment', {
                        revenueEntry: this.entryId,
                    }),
                    { text: text }
                )
                .then(() => {
                    this.refreshData();
                })
                .finally(() => (this.isUploading = false));
        },
        updateLineItems() {
            if (!this.entry || !this.lineItemsAreValid || this.isLoading) {
                return;
            }

            this.postLineItems();
        },
        postLineItems: debounce(function () {
            axios
                .post(
                    route('api.account.event.revenue.update', {
                        revenueEntry: this.entry.id,
                    }),
                    {
                        line_items: this.entry.line_items,
                    }
                )
                .then(({ data }) => {
                    if (this.entry) {
                        this.entry.subtotal = data.subtotal;
                        this.entry.total = data.total;
                    }
                    this.initialiseInvoicesTable();
                    this.reloadRelatedComponents();
                });
        }, 1500),
        viewInvoice(customerInvoiceId) {
            Eventbus.$emit('viewInvoice', {
                type: 'CustomerInvoice',
                routeData: {
                    revenueEntry: this.entry.id,
                    customerInvoice: customerInvoiceId,
                },
            });
        },
        resetState() {
            this.isLoading = true;
            this.isUploading = false;
            this.entryId = 0;
            this.entry = null;
            this.invoicesTable = null;
            this.invoices = [];
        },
        reloadRelatedComponents() {
            Eventbus.$emit('reload:entries');
        },
    },
};
</script>
